// Shared styles for the auth flow (LoginPage / ForgotPasswordPage /
// ResetPasswordPage). Single source of truth so the three pages can't drift.
//
// Brand palette from the Peak Tech logo card: light sage-mist background,
// deep sea-green accent (mermaid tail), near-black text. Error/success boxes
// keep semantic red/green — don't green-wash state colors.

export const colors = {
  pageBg: 'linear-gradient(160deg, #E6ECEA 0%, #D5DEDA 100%)',
  primary: '#3F6B5C',
  primaryHover: '#345A4D',
  primaryLoading: '#9DB4AB', // desaturated brand green for the loading state
  inputBorder: 'var(--field-edge)', // tokens.css — 3:1 field edge (design-system C19)
  text: '#1F2421',
  muted: '#5C6B64', // ≥4.5:1 on white (WCAG AA for small text)
  divider: '#E3E9E6',
};

export const container = {
  minHeight: '100dvh', // 100vh overflows behind mobile browser chrome
  background: colors.pageBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16, // keeps the card floating on small screens, corners never clip
  fontFamily: "'Assistant', 'Segoe UI', system-ui, sans-serif",
  direction: 'rtl',
};

export const card = {
  background: '#fff',
  borderRadius: 20,
  padding: 'clamp(28px, 7vw, 48px) clamp(20px, 6vw, 40px)',
  width: '100%',
  maxWidth: 380,
  boxShadow: '0 12px 40px rgba(46, 64, 57, 0.14)',
  boxSizing: 'border-box',
};

export const label = {
  display: 'block',
  fontSize: '0.88rem',
  fontWeight: 600,
  color: colors.text,
  marginBottom: 6,
};

// 1rem (16px) font — anything smaller triggers iOS Safari auto-zoom on focus.
export const input = {
  width: '100%',
  padding: '11px 14px',
  fontSize: '1rem',
  border: `1.5px solid ${colors.inputBorder}`,
  borderRadius: 10,
  // No `outline: 'none'`: the app-wide 2px green :focus-visible ring
  // (index.css, design-system C3) is the focus indicator here too. The old
  // 3px glow was ~1.3:1 against the white card — under WCAG's 3:1.
  boxSizing: 'border-box',
  direction: 'ltr',
  textAlign: 'left',
  transition: 'border-color 0.2s',
};

// Localize the browser's native constraint-validation bubbles to Hebrew.
// Without this the browser shows its own English messages ("Please fill out
// this field", "Please include an '@'…") because those follow the browser's
// language, not the page. onInvalid sets a Hebrew message per failure type;
// onInput clears it so the field can re-validate on the next submit.
export const validationHandlers = {
  onInvalid: e => {
    const t = e.target;
    if (t.validity.valueMissing) {
      t.setCustomValidity(t.type === 'email' ? 'יש להזין כתובת אימייל' : 'יש להזין סיסמה');
    } else if (t.validity.typeMismatch) {
      t.setCustomValidity('כתובת האימייל אינה תקינה');
    } else if (t.validity.tooShort) {
      t.setCustomValidity('הסיסמה חייבת להכיל לפחות 6 תווים');
    } else {
      t.setCustomValidity('');
    }
  },
  onInput: e => e.target.setCustomValidity(''),
};

export const inputFocusHandlers = {
  onFocus: e => {
    e.target.style.borderColor = colors.primary;
  },
  onBlur: e => {
    e.target.style.borderColor = colors.inputBorder;
  },
};

export const button = loading => ({
  width: '100%',
  padding: '13px',
  fontSize: '1rem',
  fontWeight: 700,
  background: loading ? colors.primaryLoading : colors.primary,
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  cursor: loading ? 'not-allowed' : 'pointer',
  transition: 'background 0.2s',
});

export const buttonHoverHandlers = {
  onMouseEnter: e => {
    if (!e.currentTarget.disabled) e.currentTarget.style.background = colors.primaryHover;
  },
  onMouseLeave: e => {
    if (!e.currentTarget.disabled) e.currentTarget.style.background = colors.primary;
  },
};

export const link = {
  color: colors.primary,
  fontSize: '0.85rem',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
};

export const errorBox = {
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#b91c1c',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: '0.88rem',
  marginBottom: 18,
  textAlign: 'center',
};

export const successBox = {
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  color: '#166534',
  borderRadius: 8,
  padding: '12px 14px',
  fontSize: '0.9rem',
  marginBottom: 18,
  textAlign: 'center',
};
